import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, subMonths, format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

interface TransactionData {
  data: { date: string; amount: number }[];
  totalRevenue: number;
  averageDaily: number;
  transactionCount: number;
}

export const useRevenueData = () => {
  const fetchTransactionData = async (startDate: Date): Promise<TransactionData> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Non authentifié");

    const { data: sales, error: salesError } = await supabase
      .from("poultry_transactions")
      .select("*")
      .eq("transaction_type", "sale")
      .gte("created_at", startDate.toISOString());

    if (salesError) throw salesError;

    const dailyTotals = sales.reduce((acc: { [key: string]: number }, sale: any) => {
      const date = format(parseISO(sale.created_at), 'dd/MM', { locale: fr });
      acc[date] = (acc[date] || 0) + sale.total_amount;
      return acc;
    }, {});

    const data = Object.entries(dailyTotals).map(([date, amount]) => ({
      date,
      amount: Number(amount),
    }));

    return {
      data,
      totalRevenue: sales.reduce((sum: number, sale: any) => sum + sale.total_amount, 0),
      averageDaily: sales.length > 0 ? sales.reduce((sum: number, sale: any) => sum + sale.total_amount, 0) / sales.length : 0,
      transactionCount: sales.length,
    };
  };

  const { data: currentMonthData } = useQuery({
    queryKey: ["transactions_summary", "current"],
    queryFn: async () => {
      const startDate = startOfMonth(new Date());
      return fetchTransactionData(startDate);
    },
  });

  const { data: previousMonthData } = useQuery({
    queryKey: ["transactions_summary", "previous"],
    queryFn: async () => {
      const startDate = startOfMonth(subMonths(new Date(), 1));
      return fetchTransactionData(startDate);
    },
  });

  const { data: validatedAmount } = useQuery({
    queryKey: ["validatedAmounts", "month"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const startDate = startOfMonth(new Date());
      
      const { data, error } = await supabase
        .from("validated_amounts")
        .select("*")
        .eq("owner_id", user.id)
        .gte("counting_date", startDate.toISOString().split("T")[0])
        .order("counting_date", { ascending: false });

      if (error) throw error;
      return data.reduce((sum, record) => sum + record.amount, 0);
    },
  });

  return {
    currentMonthData,
    previousMonthData,
    validatedAmount,
  };
};